import { useCallback, useEffect, useRef } from 'react';

import { CHECK_INTERVAL_MS, SESSION_START_KEY } from './styles';
import type { UseAbsoluteTimeoutOptions, UseAbsoluteTimeoutReturn } from './types';

/**
 * Checks elapsed time against timeout/warning thresholds and fires callbacks
 */
const checkTimeoutAndWarning = ({
  elapsed,
  timeoutMs,
  warningMs,
  warningFiredRef,
  onWarningRef,
  onTimeoutRef,
}: {
  elapsed: number;
  timeoutMs: number;
  warningMs: number;
  warningFiredRef: React.MutableRefObject<boolean>;
  onWarningRef: React.MutableRefObject<() => void>;
  onTimeoutRef: React.MutableRefObject<() => void>;
}): void => {
  if (elapsed >= timeoutMs) {
    onTimeoutRef.current();
    return;
  }

  if (elapsed >= timeoutMs - warningMs && !warningFiredRef.current) {
    warningFiredRef.current = true;
    onWarningRef.current();
  }
};

export const useAbsoluteTimeout = ({
  timeoutMs,
  warningMs,
  onWarning,
  onTimeout,
  enabled,
}: UseAbsoluteTimeoutOptions): UseAbsoluteTimeoutReturn => {
  const warningFiredRef = useRef(false);
  const onWarningRef = useRef(onWarning);
  const onTimeoutRef = useRef(onTimeout);

  onWarningRef.current = onWarning;
  onTimeoutRef.current = onTimeout;

  const resetAbsoluteWarning = useCallback(() => {
    warningFiredRef.current = false;
  }, []);

  useEffect(() => {
    if (!enabled) {
      sessionStorage.removeItem(SESSION_START_KEY);
      return;
    }

    // Persist session start in sessionStorage so page refreshes don't reset the clock
    let startTime = Number(sessionStorage.getItem(SESSION_START_KEY));
    if (!startTime || isNaN(startTime)) {
      startTime = Date.now();
      sessionStorage.setItem(SESSION_START_KEY, String(startTime));
    }

    const intervalId = setInterval(() => {
      const elapsed = Date.now() - startTime;
      checkTimeoutAndWarning({
        elapsed,
        timeoutMs,
        warningMs,
        warningFiredRef,
        onWarningRef,
        onTimeoutRef,
      });
    }, CHECK_INTERVAL_MS);

    // Check immediately on visibility change
    const handleVisibilityChange = (): void => {
      if (document.visibilityState !== 'visible') return;
      const elapsed = Date.now() - startTime;
      checkTimeoutAndWarning({
        elapsed,
        timeoutMs,
        warningMs,
        warningFiredRef,
        onWarningRef,
        onTimeoutRef,
      });
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [enabled, timeoutMs, warningMs]);

  return { resetAbsoluteWarning };
};
